import { create } from "zustand";
import {
  currentUser,
  listConnections,
  login as apiLogin,
  logout as apiLogout,
  runQueryBatch,
  setUnauthorizedHandler,
} from "./api";
import { inferColumns, sourceKey } from "./lib/data";
import {
  buildFinanceDashboard,
  DEFAULT_FINANCE_FILTERS,
  EMPTY_FINANCE_OPTIONS,
  FINANCE_ENGINE,
  FINANCE_SLICER_SQL,
  parseFinanceSlicerOptions,
  type FinanceFilters,
  type FinancePage,
  type FinanceSlicerOptions,
} from "./lib/finance";
import type {
  Connection,
  Dashboard,
  DataSource,
  DataState,
  Row,
  UserView,
} from "./types";

/** `checking` while the session resolves at boot; `anon` shows login. */
export type AuthStatus = "checking" | "authed" | "anon";

/**
 * Row budget for the whole client cache.
 *
 * Keyed by SQL text, and every slicer change mints new keys, so without a bound
 * a long session accumulates every filter state ever visited. At a 100,000-row
 * server cap a handful of wide tables is enough to exhaust the tab.
 *
 * Counted in rows rather than entries because entries differ by three orders of
 * magnitude: a four-row KPI and a 100k-row detail table are not interchangeable.
 */
const CACHE_ROW_BUDGET = 400_000;

/** Drop the oldest entries until the cache fits its budget.
 *
 *  Insertion order is eviction order (JS objects preserve it for string keys),
 *  so this is FIFO. For a dashboard that is effectively LRU — a page's widgets
 *  are fetched together and age together — without tracking access times. */
function evictOldest(cache: Record<string, DataState>): Record<string, DataState> {
  let total = 0;
  for (const e of Object.values(cache)) total += e.rows.length;
  if (total <= CACHE_ROW_BUDGET) return cache;

  const out = { ...cache };
  for (const key of Object.keys(out)) {
    if (total <= CACHE_ROW_BUDGET) break;
    total -= out[key].rows.length;
    delete out[key];
  }
  return out;
}

interface State {
  // ---- session ----------------------------------------------------------
  user: UserView | null;
  authStatus: AuthStatus;
  authBusy: boolean;
  authError: string | null;

  // ---- data -------------------------------------------------------------
  connections: Connection[];
  activeConnectionId: string | null;
  dataCache: Record<string, DataState>;

  // ---- dashboard --------------------------------------------------------
  page: FinancePage;
  filters: FinanceFilters;
  slicerOptions: FinanceSlicerOptions;
  dashboard: Dashboard;

  bootAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearSession: () => void;

  loadConnections: () => Promise<void>;
  setActiveConnection: (id: string) => void;
  loadSlicerOptions: () => Promise<void>;

  setPage: (page: FinancePage) => void;
  setFilter: (patch: Partial<FinanceFilters>) => void;

  /** Fetch every widget on the current page in ONE request. */
  loadPage: (force?: boolean) => Promise<void>;
}

const LAST_CONN_KEY = "hle.lastConnectionId";
const readLastConnection = (): string | null => {
  try {
    return localStorage.getItem(LAST_CONN_KEY);
  } catch {
    return null;
  }
};

export const useStore = create<State>((set, get) => ({
  user: null,
  authStatus: "checking",
  authBusy: false,
  authError: null,

  connections: [],
  activeConnectionId: null,
  dataCache: {},

  page: "payout",
  filters: DEFAULT_FINANCE_FILTERS,
  slicerOptions: EMPTY_FINANCE_OPTIONS,
  dashboard: buildFinanceDashboard("payout", DEFAULT_FINANCE_FILTERS),

  // ---- session ----------------------------------------------------------

  bootAuth: async () => {
    const user = await currentUser();
    if (!user) {
      set({ authStatus: "anon", user: null });
      return;
    }
    set({ user, authStatus: "authed" });
    await get().loadConnections();
  },

  login: async (email, password) => {
    set({ authBusy: true, authError: null });
    try {
      const user = await apiLogin(email, password);
      set({ user, authStatus: "authed", authBusy: false });
      await get().loadConnections();
    } catch (e) {
      set({ authBusy: false, authError: String(e instanceof Error ? e.message : e) });
    }
  },

  logout: async () => {
    try {
      await apiLogout();
    } finally {
      get().clearSession();
    }
  },

  clearSession: () =>
    set({
      user: null,
      authStatus: "anon",
      connections: [],
      activeConnectionId: null,
      dataCache: {},
      slicerOptions: EMPTY_FINANCE_OPTIONS,
    }),

  // ---- connections ------------------------------------------------------

  loadConnections: async () => {
    let list: Connection[] = [];
    try {
      list = await listConnections();
    } catch {
      // Surfaced per-widget; a failed list must not block the shell.
      return;
    }
    // This build reads ClickHouse only, so anything else cannot serve these
    // pages — showing it in the picker would only invite a confusing failure.
    const usable = list.filter((c) => c.engine === FINANCE_ENGINE);
    const remembered = readLastConnection();
    const active =
      (remembered && usable.some((c) => c.id === remembered) ? remembered : null) ??
      usable[0]?.id ??
      null;

    set({ connections: usable, activeConnectionId: active });
    if (active) {
      await get().loadSlicerOptions();
      await get().loadPage();
    }
  },

  setActiveConnection: (id) => {
    try {
      localStorage.setItem(LAST_CONN_KEY, id);
    } catch {
      /* a non-persisted preference is not worth failing over */
    }
    // Cached rows belong to the previous connector; keeping them would show
    // one source's numbers under another's name.
    set({ activeConnectionId: id, dataCache: {} });
    void get().loadSlicerOptions();
    void get().loadPage();
  },

  loadSlicerOptions: async () => {
    const connId = get().activeConnectionId;
    if (!connId) return;
    try {
      const [res] = await runQueryBatch(connId, [
        { id: "slicers", sql: FINANCE_SLICER_SQL },
      ]);
      if (res?.result) {
        set({ slicerOptions: parseFinanceSlicerOptions(res.result.rows) });
      }
    } catch {
      /* best-effort: empty slicers are usable, a blocked page is not */
    }
  },

  // ---- dashboard --------------------------------------------------------

  setPage: (page) => {
    set((s) => ({ page, dashboard: buildFinanceDashboard(page, s.filters) }));
    void get().loadPage();
  },

  setFilter: (patch) => {
    set((s) => {
      const filters = { ...s.filters, ...patch };
      return { filters, dashboard: buildFinanceDashboard(s.page, filters) };
    });
    void get().loadPage();
  },

  loadPage: async (force = false) => {
    const { activeConnectionId: connId, dashboard, dataCache } = get();
    if (!connId) return;

    // One entry per DISTINCT source, so widgets sharing SQL (the KPI row) are
    // asked for once. Already-cached sources are skipped unless forced.
    const wanted = new Map<string, DataSource>();
    for (const w of dashboard.widgets) {
      const key = sourceKey(w.source, connId);
      if (!force && dataCache[key]?.status === "ok") continue;
      wanted.set(key, w.source);
    }
    if (wanted.size === 0) return;

    // Mark loading up front so the page shows spinners rather than stale rows.
    set((s) => {
      const next = { ...s.dataCache };
      for (const key of wanted.keys()) {
        next[key] = { status: "loading", columns: [], rows: [] };
      }
      return { dataCache: next };
    });

    const queries = [...wanted].map(([key, source]) => ({ id: key, sql: source.query }));

    let results;
    try {
      results = await runQueryBatch(connId, queries);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      set((s) => {
        const next = { ...s.dataCache };
        for (const key of wanted.keys()) {
          next[key] = { status: "error", columns: [], rows: [], error: message };
        }
        return { dataCache: next };
      });
      return;
    }

    set((s) => {
      const next = { ...s.dataCache };
      for (const r of results) {
        if (r.error || !r.result) {
          next[r.id] = {
            status: "error",
            columns: [],
            rows: [],
            error: r.error ?? "no result",
          };
          continue;
        }
        const rows = r.result.rows as Row[];
        next[r.id] = {
          status: "ok",
          columns: inferColumns(r.result.columns, rows),
          rows,
          elapsedMs: r.result.elapsedMs,
          truncated: r.result.truncated,
        };
      }
      return { dataCache: evictOldest(next) };
    });
  },
}));

// A 401 anywhere drops straight back to the login screen. Registered here
// rather than in api.ts, which must not import the store back (circular).
setUnauthorizedHandler(() => useStore.getState().clearSession());
